import type { AlbumConfig, StickerInvestment } from '../types'
import { formatCurrency, daysUntil, sequenceTarget, hasInvestmentThisMonth, investmentCountThisMonth, stickerValue } from '../data'
import { useI18n } from '../i18n/context'

interface Props {
  album: AlbumConfig
  stickers: Record<number, StickerInvestment>
  isTargetVisible: boolean
  onToggleTarget: () => void
}

export function MetricsBar({ album, stickers, isTargetVisible, onToggleTarget }: Props): JSX.Element {
  const { t } = useI18n()
  const collectedCount = Object.keys(stickers).length
  const investedAmount = Object.values(stickers).reduce(
    (sum, s) => sum + stickerValue(s.stickerNumber, album.size), 0
  )
  const targetAmount = sequenceTarget(album.size)
  const investmentProgress = targetAmount > 0 ? investedAmount / targetAmount : 0
  const stickerProgress = album.size > 0 ? collectedCount / album.size : 0

  const days = daysUntil(album.targetFinishDate)
  const activeThisMonth = hasInvestmentThisMonth(stickers)
  const monthCount = investmentCountThisMonth(stickers)

  function daysLabel(): string | null {
    if (days === null) return null
    if (days < 0) return t.daysOverdue(Math.abs(days))
    if (days === 0) return t.targetToday
    return t.daysRemaining(days)
  }

  return (
    <section className="metrics-panel">
      <article className="metric-card">
        <span>{t.collectedStickers}</span>
        <strong>{collectedCount} / {album.size}</strong>
        <div className="meter">
          <div className="meter-fill" style={{ width: `${Math.min(stickerProgress * 100, 100)}%` }} />
        </div>
      </article>

      <article className="metric-card">
        <span>{t.investedAmount}</span>
        <strong>{formatCurrency(investedAmount, album.currency)}</strong>
        <div className="meter">
          <div className="meter-fill" style={{ width: `${Math.min(investmentProgress * 100, 100)}%` }} />
        </div>
        {isTargetVisible && (
          <p className="metric-target">{t.ofTarget}: {formatCurrency(targetAmount, album.currency)}</p>
        )}
        <button className="ghost-button metric-toggle" onClick={onToggleTarget} type="button">
          {isTargetVisible ? t.hideTarget : t.showTarget}
        </button>
      </article>

      <article className="metric-card">
        <span>{t.targetFinishDate}</span>
        <strong>
          {album.targetFinishDate
            ? new Date(album.targetFinishDate + 'T00:00:00').toLocaleDateString(undefined, {
                year: 'numeric', month: 'short', day: 'numeric'
              })
            : t.targetNotDefined}
        </strong>
        {daysLabel() && (
          <p className={`metric-days ${days !== null && days < 0 ? 'is-overdue' : 'is-ontrack'}`}>
            {daysLabel()}
          </p>
        )}
      </article>

      <article className="metric-card metric-card-monthly">
        <span>{t.thisMonth}</span>
        <div className="monthly-status">
          <span className={`status-dot ${activeThisMonth ? 'is-green' : 'is-red'}`} />
          <strong>
            {activeThisMonth ? t.investmentsThisMonth(monthCount) : t.noInvestments}
          </strong>
        </div>
        <p className={`monthly-label ${activeThisMonth ? 'text-green' : 'text-red'}`}>
          {activeThisMonth ? t.activeMonth : t.noActivityYet}
        </p>
      </article>
    </section>
  )
}
